import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import GuideLayout from "./GuideLayout";

const faqItems = [
  {
    q: "How do I find GitHub developers by skill or location?",
    a: "Use the main search box with keywords that reflect skills or roles, then open Advanced filters. Add a location, choose a primary language, set minimum followers if you need established profiles, and use last-push style constraints to favour active contributors. GitHub search qualifiers are applied server-side so results stay relevant.",
  },
  {
    q: "Can I search GitHub repositories here?",
    a: "Yes. Choose Repositories mode on the home page and enter keywords (topics, stack, or product name). You will see repository cards with stars, forks, language, and last updated time. Sort results—by stars or recent activity—and open View details for more context or go straight to GitHub.",
  },
  {
    q: "How fresh are the results?",
    a: "Results are pulled from public repository and profile data and refreshed through normal search actions. If you do not see what you expect, broaden keywords, adjust filters, or re-run the query.",
  },
  {
    q: "Does this replace GitHub’s own search?",
    a: "No. This interface is an opinionated wrapper: filters, cards, and insights are tailored for quick scanning. For complex queries or private repos, use github.com or the API directly.",
  },
  {
    q: "How are languages detected?",
    a: "Repository language reflects GitHub’s linguist statistics when available. Profiles may show primary languages derived from public repositories—useful as a signal, not a complete skill inventory.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqItems.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.a,
    },
  })),
};

const META_DESC =
  "FAQ: find developers by skill or location, search repositories, and understand how results and filters work.";

const GuideFaqPage = () => {
  useEffect(() => {
    const id = "gf-faq-page-jsonld";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.id = id;
      el.type = "application/ld+json";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(faqSchema);
    return () => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
    };
  }, []);

  return (
    <GuideLayout
      title="FAQ: GitHub search"
      description={META_DESC}
      eyebrow="Help"
      breadcrumb={[
        { to: "/", label: "Home" },
        { to: "/guide", label: "Guides" },
        { label: "FAQ" },
      ]}
    >
      <div className="gf-guide-faq">
        <p className="gf-guide-prose__lead">
          Quick answers about developer and repository search, filters, and result interpretation.
        </p>
        <dl className="gf-faq-page-list">
          {faqItems.map((item) => (
            <div key={item.q} className="gf-faq-page-item">
              <dt>{item.q}</dt>
              <dd>{item.a}</dd>
            </div>
          ))}
        </dl>
        <p className="gf-guide-prose__cta">
          <Link to="/guide" className="gf-btn gf-btn--outline gf-btn--sm">
            All guides
          </Link>
          <Link to="/" className="gf-btn gf-btn--primary gf-btn--sm">
            Back to search
          </Link>
        </p>
      </div>
    </GuideLayout>
  );
};

export default GuideFaqPage;
